import { useEffect, useState } from 'react'
import { api, type ArtefactSummary, type TopicSummary } from './api.ts'
import { readCache, writeCache } from './cache.ts'

const KEY = 'catalog'
const TOPICS_KEY = 'topics'
const ERROR = 'Could not load the catalog. Please try again.'

export type CatalogState = {
  /**
   * null means "not loaded yet" — show a skeleton. An empty array means the
   * catalog really is empty. Conflating the two is what made a slow load look
   * identical to no tools at all.
   */
  items: ArtefactSummary[] | null
  /**
   * Topics, on the same null-means-loading contract. Fetched alongside the
   * artefacts rather than by each view, because every listing needs both to
   * collapse a topic into a single card (buildEntries) and a view holding one
   * without the other would render a bundle as loose documents.
   */
  topics: TopicSummary[] | null
  error: string | null
}

/**
 * The catalog, stale-while-revalidate: whatever was cached paints on the first
 * render, and a background fetch replaces it.
 */
export function useCatalog(): CatalogState {
  // Seeded from the cache during the initial render — not in an effect — so
  // cached cards are in the very first paint rather than one frame later.
  const [items, setItems] = useState<ArtefactSummary[] | null>(() =>
    readCache<ArtefactSummary[]>(KEY),
  )
  const [topics, setTopics] = useState<TopicSummary[] | null>(() =>
    readCache<TopicSummary[]>(TOPICS_KEY),
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    // One Promise.all, not two independent fetches: a render holding fresh
    // artefacts against stale topics would misplace documents between bundles.
    Promise.all([api.catalog(), api.topics()])
      .then(([list, topicList]) => {
        if (!live) return
        setItems(list)
        setTopics(topicList)
        setError(null)
        writeCache(KEY, list)
        writeCache(TOPICS_KEY, topicList)
      })
      .catch(() => {
        // A failed revalidation must not destroy good data: if we're already
        // showing cached cards, keep them and stay quiet. Only surface the
        // error when there is nothing to show instead.
        if (live && readCache<ArtefactSummary[]>(KEY) === null) setError(ERROR)
      })
    return () => {
      live = false
    }
  }, [])

  return { items, topics, error }
}
